import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Typography,
  Snackbar,
} from '@mui/joy';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import NativeCheckbox from '../../../main/components/NativeCheckbox';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import {
  patchShipmentAppliedRule,
  postShipmentAppliedRule,
  deleteShipmentAppliedRule,
  recalculateSingleShipmentPrice,
  getShipmentPriceExplain,
} from '../../../main/API/shipment';
import { masterApiErrorMessage } from '../../../main/utils/masterApiError';
import OmrSymbol from '../../../main/components/OmrSymbol';
import DeleteAlert from '../../../main/widgets/DeleteAlert';
import SettlementGroupOptionList from '../../../main/widgets/SettlementGroupOptionList';

function normalizeAppliedRows(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.results)) return raw.results;
  return [];
}

/**
 * Map local staged draft rows → create-payload `manual_lines[]`.
 * Drops incomplete placeholders (no amount, or neither rule nor label).
 */
export function mapStagedRowsToManualLines(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const out = [];
  for (const row of rows) {
    if (!row) continue;
    const amount = String(row.amount != null ? row.amount : '').trim();
    if (!amount) continue;
    const label = String(row.label != null ? row.label : '').trim();
    const labelAr = String(row.label_ar != null ? row.label_ar : '').trim();
    const ridRaw = row.rule != null && row.rule !== '' ? String(row.rule).trim() : '';
    const rule = ridRaw ? parseInt(ridRaw, 10) : NaN;
    const hasRule = Number.isFinite(rule);
    if (!label && !hasRule) continue;
    const item = { amount };
    if (hasRule) item.rule = rule;
    if (label) item.label = label;
    if (labelAr) item.label_ar = labelAr;
    item.note = String(row.note != null ? row.note : '').trim();
    const sgRaw =
      row.settlement_group != null && typeof row.settlement_group === 'object'
        ? row.settlement_group.id
        : row.settlement_group;
    const sg = sgRaw != null && sgRaw !== '' ? parseInt(String(sgRaw), 10) : NaN;
    if (Number.isFinite(sg)) item.settlement_group = sg;
    out.push(item);
  }
  return out;
}

/**
 * Base- and addon-phase rows are engine-owned: their amount comes from the pricing
 * engine and must not be hand-edited or deleted from the shipment form. Operators
 * may still toggle excluded or update the note, and can adjust totals via manual
 * ledger lines.
 */
function isAmountLockedRow(row) {
  if (!row) return false;
  const scope = String(row.scope_snapshot != null ? row.scope_snapshot : '').toLowerCase();
  return scope === 'base' || scope === 'addon';
}

/**
 * Applied-rules ledger panel.
 *
 * Edit mode (`draftMode=false`, `shipmentId` set): full server ledger
 * (explain / recalc / attach / patch / delete).
 *
 * Create/draft mode (`draftMode=true`): local staged rows only — no server
 * explain/recalc/edit/delete. Parent includes them as `manual_lines` on create.
 */
export default function ShipmentAppliedRulesPanel({
  shipmentId,
  appliedRules = [],
  onRefresh,
  draftMode = false,
  stagedRows = null,
  onStagedChange = null,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canViewExplain = hasPermission(PERMISSIONS.SHIPMENTS_VIEW);
  const canManageLedger = hasPermission(PERMISSIONS.SHIPMENTS_APPLIED_RULES_MANAGE);
  const canRecalc = hasPermission(PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE);

  const isDraft = Boolean(draftMode) || !shipmentId;
  const draftIdRef = useRef(1);

  const [serverRows, setServerRows] = useState(() => normalizeAppliedRows(appliedRules));
  const [localStaged, setLocalStaged] = useState([]);
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainJson, setExplainJson] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [manualLabel, setManualLabel] = useState('');
  const [manualLabelAr, setManualLabelAr] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [manualRuleId, setManualRuleId] = useState('');
  const [manualSettlementGroup, setManualSettlementGroup] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', color: 'success' });
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editExcluded, setEditExcluded] = useState(false);
  const [editNote, setEditNote] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const controlledDraft = isDraft && Array.isArray(stagedRows) && typeof onStagedChange === 'function';
  const draftRows = controlledDraft ? stagedRows : localStaged;

  const setDraftRows = useCallback(
    (next) => {
      if (controlledDraft) {
        onStagedChange(next);
      } else {
        setLocalStaged(next);
      }
    },
    [controlledDraft, onStagedChange]
  );

  React.useEffect(() => {
    if (!isDraft) {
      setServerRows(normalizeAppliedRows(appliedRules));
    }
  }, [appliedRules, isDraft]);

  const rows = isDraft ? draftRows : serverRows;

  const showSnack = useCallback(
    (msg, color = 'success') => setSnack({ open: true, msg, color }),
    []
  );

  const openEdit = useCallback((row) => {
    setEditRow(row);
    setEditAmount(String(row.amount != null ? row.amount : ''));
    setEditExcluded(Boolean(row.excluded));
    setEditNote(row.note != null ? row.note : '');
  }, []);

  const requestDelete = useCallback(
    (row) => {
      if (!canManageLedger) return;
      if (!isDraft && isAmountLockedRow(row)) {
        showSnack(t('pricingRules.cannotDeleteBaseApplied'), 'warning');
        return;
      }
      setRowToDelete(row);
      setDeleteAlertOpen(true);
    },
    [canManageLedger, isDraft, t, showSnack]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!rowToDelete || !canManageLedger) {
      setDeleteAlertOpen(false);
      setRowToDelete(null);
      return;
    }

    if (isDraft) {
      setDraftRows((prev) => (prev || []).filter((r) => r.id !== rowToDelete.id));
      setDeleteAlertOpen(false);
      setRowToDelete(null);
      showSnack(t('pricingRules.arDeleted'));
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteShipmentAppliedRule(shipmentId, rowToDelete.id);
      setDeleteAlertOpen(false);
      setRowToDelete(null);
      showSnack(t('pricingRules.arDeleted'));
      if (onRefresh) onRefresh();
    } catch (e) {
      showSnack(masterApiErrorMessage(e, t), 'danger');
    } finally {
      setDeleteLoading(false);
    }
  }, [
    rowToDelete,
    canManageLedger,
    isDraft,
    setDraftRows,
    shipmentId,
    t,
    onRefresh,
    showSnack,
  ]);

  const handleExplain = async () => {
    if (isDraft || !shipmentId || !canViewExplain) return;
    try {
      const res = await getShipmentPriceExplain(shipmentId);
      setExplainJson(JSON.stringify(res.data, null, 2));
      setExplainOpen(true);
    } catch (e) {
      showSnack(masterApiErrorMessage(e, t), 'danger');
    }
  };

  const handleRecalc = async () => {
    if (isDraft || !shipmentId || !canRecalc) return;
    try {
      await recalculateSingleShipmentPrice(shipmentId);
      showSnack(t('pricingRules.recalcShipmentOk'));
      if (onRefresh) onRefresh();
    } catch (e) {
      showSnack(masterApiErrorMessage(e, t), 'danger');
    }
  };

  const openAddManual = () => {
    setManualLabel('');
    setManualLabelAr('');
    setManualAmount('');
    setManualNote('');
    setManualRuleId('');
    setManualSettlementGroup(null);
    setAddOpen(true);
  };

  const submitManual = async () => {
    if (!canManageLedger) return;
    const rid = String(manualRuleId || '').trim();
    const ruleNum = rid ? parseInt(rid, 10) : NaN;
    const label = manualLabel.trim();
    const labelAr = manualLabelAr.trim();
    if (!Number.isFinite(ruleNum) && !label) {
      showSnack(t('pricingRules.manualLabelOrRule'), 'warning');
      return;
    }
    const amount = manualAmount.trim() || '0';
    const note = manualNote.trim();
    const sgId =
      manualSettlementGroup && manualSettlementGroup.id != null
        ? Number(manualSettlementGroup.id)
        : NaN;

    if (isDraft) {
      const draftId = `draft-${draftIdRef.current++}`;
      const staged = {
        id: draftId,
        rule: Number.isFinite(ruleNum) ? ruleNum : null,
        rule_code: Number.isFinite(ruleNum) ? String(ruleNum) : '',
        scope_snapshot: 'manual',
        label,
        label_ar: labelAr,
        amount,
        auto_applied: false,
        excluded: false,
        note,
        settlement_group: Number.isFinite(sgId) ? sgId : null,
        settlement_group_name_snapshot:
          (manualSettlementGroup && manualSettlementGroup.name) || '',
      };
      setDraftRows((prev) => [...(prev || []), staged]);
      setAddOpen(false);
      showSnack(t('pricingRules.manualStaged'));
      return;
    }

    if (!shipmentId) return;
    const body = {};
    if (Number.isFinite(ruleNum)) body.rule = ruleNum;
    if (!body.rule) body.label = label;
    else if (label) body.label = label;
    if (labelAr) body.label_ar = labelAr;
    body.amount = amount;
    body.note = note;
    if (Number.isFinite(sgId)) body.settlement_group = sgId;

    setAdding(true);
    try {
      await postShipmentAppliedRule(shipmentId, body);
      setAddOpen(false);
      showSnack(t('pricingRules.manualAttached'));
      if (onRefresh) onRefresh();
    } catch (e) {
      showSnack(masterApiErrorMessage(e, t), 'danger');
    } finally {
      setAdding(false);
    }
  };

  // Per-piece grouping: detect whether any row carries a shipment_piece id (or
  // a nested object). When present, the panel shows a "Piece" column so the
  // operator can see which box the rate applies to (per the per-piece pricing
  // rollup release: applied_rules[i].shipment_piece).
  const hasPerPieceRows = useMemo(
    () =>
      !isDraft &&
      rows.some(
        (r) =>
          r &&
          (r.shipment_piece != null ||
            (r.shipment_piece_label != null && r.shipment_piece_label !== ''))
      ),
    [rows, isDraft]
  );

  const columns = useMemo(
    () => [
      {
        field: 'rule_code',
        headerName: t('pricingRules.arColRule'),
        flex: 1,
        minWidth: 130,
      },
      ...(hasPerPieceRows
        ? [
            {
              field: 'shipment_piece',
              headerName: t('pricingRules.arColPiece', { defaultValue: 'Piece' }),
              flex: 0.7,
              minWidth: 110,
              renderCell: (p) => {
                const row = p.row || {};
                const sp = row.shipment_piece;
                const label =
                  row.shipment_piece_label ||
                  (sp && typeof sp === 'object' && (sp.human_label || sp.barcode)) ||
                  (sp != null ? `#${sp}` : '—');
                return (
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {label}
                  </Typography>
                );
              },
            },
          ]
        : []),
      { field: 'label', headerName: t('pricingRules.arColLabel'), flex: 1.2, minWidth: 140 },
      {
        field: 'settlement_group_name_snapshot',
        headerName: t('pricingRules.arColSettlementGroup'),
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (p) => {
          const row = p.row || {};
          return (
            row.settlement_group_name_snapshot
            || row.settlement_group_code_snapshot
            || row.settlement_group_name
            || ''
          );
        },
      },
      {
        field: 'amount',
        headerName: t('pricingRules.arColAmount'),
        flex: 0.9,
        minWidth: 110,
        renderCell: (p) => (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, py: 0.5 }}>
            <span>{p.row.amount != null ? p.row.amount : ''}</span>
            <OmrSymbol height={14} />
          </Box>
        ),
      },
      {
        field: 'scope_snapshot',
        headerName: t('pricingRules.arColScope'),
        flex: 0.8,
        minWidth: 90,
      },
      {
        field: 'auto_applied',
        headerName: t('pricingRules.arColAuto'),
        flex: 0.7,
        minWidth: 80,
        renderCell: (p) =>
          p.row.auto_applied ? t('serviceManager.priceYes') : t('serviceManager.priceNo'),
      },
      ...(isDraft
        ? []
        : [
            {
              field: 'excluded',
              headerName: t('pricingRules.arColExcluded'),
              flex: 0.7,
              minWidth: 80,
              renderCell: (p) =>
                p.row.excluded ? t('serviceManager.priceYes') : t('serviceManager.priceNo'),
            },
          ]),
      {
        field: 'note',
        headerName: t('pricingRules.arColNote'),
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        renderCell: (p) => {
          const note = p.row.note != null ? String(p.row.note) : '';
          if (!note) return '';
          return (
            <Box
              title={note}
              sx={{
                py: 0.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
            >
              {note}
            </Box>
          );
        },
      },
      {
        field: '_actions',
        headerName: t('pricingRules.arColActions'),
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        renderCell: (p) =>
          canManageLedger ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
              {!isDraft ? (
                <Button size="sm" variant="soft" onClick={() => openEdit(p.row)}>
                  {t('actions.edit')}
                </Button>
              ) : null}
              {isDraft || !isAmountLockedRow(p.row) ? (
                <Button size="sm" variant="plain" color="danger" onClick={() => requestDelete(p.row)}>
                  {t('actions.delete')}
                </Button>
              ) : null}
            </Box>
          ) : (
            ''
          ),
      },
    ],
    [canManageLedger, hasPerPieceRows, isDraft, t, openEdit, requestDelete]
  );

  const saveEdit = async () => {
    if (!editRow || !canManageLedger || isDraft) return;
    setSavingEdit(true);
    try {
      const payload = {
        excluded: editExcluded,
        note: editNote.trim(),
      };
      if (!isAmountLockedRow(editRow)) {
        payload.amount = editAmount.trim();
      }
      await patchShipmentAppliedRule(shipmentId, editRow.id, payload);
      setEditRow(null);
      showSnack(t('pricingRules.arUpdated'));
      if (onRefresh) onRefresh();
    } catch (e) {
      showSnack(masterApiErrorMessage(e, t), 'danger');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography level="title-lg" sx={{ mb: 1 }}>
        {t('pricingRules.sectionAppliedRules')}
      </Typography>
      <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
        {isDraft
          ? t('pricingRules.sectionAppliedRulesHintDraft')
          : t('pricingRules.sectionAppliedRulesHint')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {!isDraft ? (
          <>
            <PermissionGate permission={PERMISSIONS.SHIPMENTS_VIEW}>
              <Button variant="outlined" size="sm" onClick={handleExplain}>
                {t('pricingRules.showPriceExplain')}
              </Button>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE}>
              <Button variant="solid" color="neutral" size="sm" onClick={handleRecalc}>
                {t('pricingRules.recalcThisShipment')}
              </Button>
            </PermissionGate>
          </>
        ) : null}
        <PermissionGate permission={PERMISSIONS.SHIPMENTS_APPLIED_RULES_MANAGE}>
          <Button variant="solid" size="sm" onClick={openAddManual}>
            {t('pricingRules.addManualLine')}
          </Button>
        </PermissionGate>
      </Box>

      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        hideFooter
        disableRowSelectionOnClick
        sx={{ width: '100%' }}
      />

      <Modal open={explainOpen} onClose={() => setExplainOpen(false)}>
        <ModalDialog
          sx={{
            maxWidth: 720,
            width: '100%',
            maxHeight: 'min(90vh, 720px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Typography level="title-lg" sx={{ flexShrink: 0 }}>
            {t('pricingRules.explainTitle')}
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 1,
              flex: '1 1 auto',
              minHeight: 120,
              maxHeight: 'min(58vh, 520px)',
              m: 0,
              p: 1.5,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: 1.5,
              bgcolor: 'background.level1',
              borderRadius: 'sm',
              border: '1px solid',
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {explainJson}
          </Box>
          <Button sx={{ mt: 1.5, flexShrink: 0 }} onClick={() => setExplainOpen(false)}>
            {t('actions.close')}
          </Button>
        </ModalDialog>
      </Modal>

      <Modal open={addOpen} onClose={() => !adding && setAddOpen(false)}>
        <ModalDialog>
          <Typography level="title-lg">
            {isDraft ? t('pricingRules.addManualTitleDraft') : t('pricingRules.addManualTitle')}
          </Typography>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualRuleIdOpt')}</FormLabel>
            <Input
              value={manualRuleId}
              onChange={(e) => setManualRuleId(e.target.value)}
              placeholder="42"
            />
          </FormControl>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualLabelReq')}</FormLabel>
            <Input value={manualLabel} onChange={(e) => setManualLabel(e.target.value)} />
          </FormControl>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualLabelArOpt')}</FormLabel>
            <Input value={manualLabelAr} onChange={(e) => setManualLabelAr(e.target.value)} />
          </FormControl>
          <Box sx={{ mt: 1 }}>
            <SettlementGroupOptionList
              key={manualSettlementGroup?.id || 'manual-sg-none'}
              label={t('pricingRules.fieldSettlementGroup')}
              defaultValue={manualSettlementGroup}
              onChange={(_, value) => setManualSettlementGroup(value)}
              activeOnly
            />
          </Box>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualAmountReq')}</FormLabel>
            <Input
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              endDecorator={<OmrSymbol height={16} />}
            />
          </FormControl>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualNoteOpt')}</FormLabel>
            <Input value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button loading={adding} onClick={submitManual}>
              {t('actions.submit')}
            </Button>
            <Button variant="plain" disabled={adding} onClick={() => setAddOpen(false)}>
              {t('actions.cancel')}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      <Modal open={Boolean(editRow) && !isDraft} onClose={() => !savingEdit && setEditRow(null)}>
        <ModalDialog>
          <Typography level="title-lg">{t('pricingRules.editAppliedTitle')}</Typography>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualAmountReq')}</FormLabel>
            <Input
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              endDecorator={<OmrSymbol height={16} />}
              disabled={isAmountLockedRow(editRow)}
              readOnly={isAmountLockedRow(editRow)}
            />
            {isAmountLockedRow(editRow) ? (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                {t('pricingRules.amountLockedForBaseAddon')}
              </Typography>
            ) : null}
          </FormControl>
          <FormControl sx={{ mt: 1 }}>
            <NativeCheckbox
              checked={editExcluded}
              onChange={(e) => setEditExcluded(e.target.checked)}
              label={t('pricingRules.excludeFromTotal')}
            />
          </FormControl>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>{t('pricingRules.manualNoteOpt')}</FormLabel>
            <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} />
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button loading={savingEdit} onClick={saveEdit}>
              {t('actions.save')}
            </Button>
            <Button variant="plain" disabled={savingEdit} onClick={() => setEditRow(null)}>
              {t('actions.cancel')}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      <DeleteAlert
        label={t('pricingRules.deleteAlertAppliedRule')}
        open={deleteAlertOpen}
        onDelete={handleDeleteConfirm}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteAlertOpen(false);
          setRowToDelete(null);
        }}
        loading={deleteLoading}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        color={snack.color}
        variant="soft"
      >
        {snack.msg}
      </Snackbar>
    </Box>
  );
}
